import React, { useState } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../context/ToastContext';
import Button from './Button';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type FeedbackType = 'bug' | 'feature_request';
type Urgency = 'nice_to_have' | 'medium' | 'urgent';

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('bug');
  const [urgency, setUrgency] = useState<Urgency>('medium');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!description.trim()) {
      toast.error('Please provide a description');
      return;
    }

    setIsSubmitting(true);

    try {
      const hostname = window.location.hostname;
      
      // Use relative URL like all other API calls in the app
      await axios.post('/feedback', {
        feedback_type: feedbackType,
        urgency: urgency,
        description: description.trim(),
        domain: hostname
      });

      toast.success('Thank you for your feedback! We\'ll review it soon.');
      setDescription('');
      setFeedbackType('bug');
      setUrgency('medium');
      onClose();
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to submit feedback. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setDescription('');
      setFeedbackType('bug');
      setUrgency('medium');
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Share Feedback
            </h3>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Feedback Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Type <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="feedbackType"
                    value="bug"
                    checked={feedbackType === 'bug'}
                    onChange={(e) => setFeedbackType(e.target.value as FeedbackType)}
                    className="w-4 h-4 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:ring-gray-500 focus:ring-2 bg-white dark:bg-gray-700"
                    disabled={isSubmitting}
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Bug</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="feedbackType"
                    value="feature_request"
                    checked={feedbackType === 'feature_request'}
                    onChange={(e) => setFeedbackType(e.target.value as FeedbackType)}
                    className="w-4 h-4 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:ring-gray-500 focus:ring-2 bg-white dark:bg-gray-700"
                    disabled={isSubmitting}
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Feature Request</span>
                </label>
              </div>
            </div>

            {/* Urgency */}
            <div>
              <label htmlFor="urgency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Urgency <span className="text-red-500">*</span>
              </label>
              <select
                id="urgency"
                value={urgency}
                onChange={(e) => setUrgency(e.target.value as Urgency)}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="nice_to_have">Nice to have</option>
                <option value="medium">Medium</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Describe the issue <span className="text-red-500">*</span>
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                required
                disabled={isSubmitting}
                placeholder="Please provide details about the bug or feature request..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed resize-y"
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                variant="primary"
                fullWidth
                disabled={isSubmitting || !description.trim()}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;

